import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface RatingItem {
  id: string;
  title: string;
  votingType: 'team' | 'individual';
  createdAt: number;
  sortOrder: number;
}

function dbRowToItem(row: Record<string, unknown>): RatingItem {
  return {
    id: row.id as string,
    title: (row.title as string) ?? '',
    votingType: (row.voting_type as 'team' | 'individual') ?? 'individual',
    createdAt: row.created_at as number,
    sortOrder: (row.sort_order as number) ?? 0,
  };
}

export function useRatingItems() {
  const [items, setItems]   = useState<RatingItem[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    const { data } = await supabase
      .from('rating_items')
      .select('*')
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (data) setItems(data.map(r => dbRowToItem(r as Record<string, unknown>)));
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchItems();
    const channel = supabase
      .channel(`rating-items-rt-${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rating_items' }, () => {
        fetchItems();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [fetchItems]);

  const addItem = useCallback(async (
    title: string,
    votingType: 'team' | 'individual',
  ) => {
    const now = Date.now();
    const row = {
      id: `item-${now}`,
      title: title.trim() || '評分項目',
      voting_type: votingType,
      created_at: now,
      sort_order: now,
    };
    setItems(prev => [...prev, dbRowToItem(row as Record<string, unknown>)]);
    await supabase.from('rating_items').insert(row);
  }, []);

  const deleteItem = useCallback(async (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
    await supabase.from('rating_items').delete().eq('id', id);
  }, []);

  const clearAllItems = useCallback(async () => {
    setItems([]);
    await supabase.from('rating_items').delete().not('id', 'is', null);
  }, []);

  return { items, loading, addItem, deleteItem, clearAllItems };
}
